import { useState } from "react";
import { toast } from "sonner";
import { UploadCloud } from "lucide-react";
import { api, asset, formatApiError } from "@/lib/api";
import { AdminCollection } from "./AdminCollection";
import { FileField } from "./FileField";
import { field, Label, FormActions } from "./formBits";

const WipForm = ({ initial, onSave, onCancel }) => {
  const [f, setF] = useState({ title: "", note: "", tag: "wip", image_url: "", order: 0, ...initial });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.image_url) return toast.error("Upload an image");
    setBusy(true);
    const { id, created_at, ...body } = f;
    await onSave({ ...body, order: Number(f.order) || 0 });
    setBusy(false);
  };

  return (
    <form onSubmit={submit} data-testid="wip-form" className="space-y-5">
      <FileField label="IMAGE *" kind="image" accept="image/*" value={f.image_url} onChange={set("image_url")} testid="wip-image" />
      <label className="block"><Label>TITLE (OPTIONAL)</Label><input data-testid="wip-title-input" value={f.title} onChange={(e) => set("title")(e.target.value)} className={field} /></label>
      <label className="block"><Label>SHORT NOTE</Label><textarea data-testid="wip-note-input" rows={3} value={f.note} onChange={(e) => set("note")(e.target.value)} className={field} /></label>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>TAG</Label>
          <div className="flex gap-2">
            {[["wip", "WIP"], ["asset", "Asset"]].map(([k, l]) => (
              <button type="button" key={k} data-testid={`wip-tag-${k}`} onClick={() => set("tag")(k)}
                className={`flex-1 py-2.5 font-mono text-[11px] tracking-[0.2em] border transition-colors ${f.tag === k ? "bg-[#F5A524] text-black border-[#F5A524]" : "border-[#F5A524]/30 text-zinc-300"}`}>{l}</button>
            ))}
          </div>
        </div>
        <label className="block"><Label>ORDER</Label><input data-testid="wip-order-input" type="number" value={f.order} onChange={(e) => set("order")(e.target.value)} className={field} /></label>
      </div>
      <FormActions busy={busy} onCancel={onCancel} label="SAVE ITEM" />
    </form>
  );
};

const Preview = ({ item }) => (
  <>
    <img src={asset(item.image_url)} alt="" className="w-16 h-16 object-cover shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="font-display font-bold uppercase text-xl truncate">{item.title || "Untitled"}</p>
      <p className="font-mono text-[11px] tracking-[0.2em] text-[#F5A524]">{item.tag === "asset" ? "ASSET" : "WIP"}</p>
    </div>
  </>
);

const titleFrom = (name) => name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim();

const BulkUpload = ({ reload }) => {
  const [tag, setTag] = useState("wip");
  const [status, setStatus] = useState(null);
  const [over, setOver] = useState(false);

  const run = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    if (!files.length) return toast.error("Drop image files (jpg, png, webp, gif)");
    let ok = 0;
    for (let i = 0; i < files.length; i++) {
      setStatus(`UPLOADING ${i + 1} / ${files.length}`);
      try {
        const fd = new FormData();
        fd.append("file", files[i]);
        const { data } = await api.post("/upload", fd);
        await api.post("/wip", { title: titleFrom(files[i].name), note: "", tag, image_url: data.url, order: 0 });
        ok += 1;
      } catch (err) {
        toast.error(`${files[i].name}: ${formatApiError(err)}`);
      }
    }
    setStatus(null);
    toast.success(`${ok} ITEM(S) ADDED`);
    reload();
  };

  return (
    <div data-testid="wip-bulk-upload" onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); if (!status) run(e.dataTransfer.files); }}
      className={`mb-8 border-2 border-dashed p-6 sm:p-8 text-center transition-colors ${over ? "border-[#F5A524] bg-[#F5A524]/10" : "border-[#F5A524]/30"}`}>
      <UploadCloud className="mx-auto text-[#F5A524]" size={32} />
      <p className="mt-3 font-display font-black uppercase text-2xl">{status || "Drop sketches here"}</p>
      <p className="mt-1 text-sm text-zinc-400">Add many at once. Each image becomes its own item, titled from its file name. Edit notes afterwards.</p>
      <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
        <span className="font-mono text-[11px] tracking-[0.2em] text-zinc-400 mr-1">TAG ALL AS</span>
        {[["wip", "WIP"], ["asset", "Asset"]].map(([k, l]) => (
          <button type="button" key={k} data-testid={`wip-bulk-tag-${k}`} onClick={() => setTag(k)}
            className={`px-4 py-2 font-mono text-[11px] tracking-[0.2em] border transition-colors ${tag === k ? "bg-[#F5A524] text-black border-[#F5A524]" : "border-[#F5A524]/30 text-zinc-300"}`}>{l}</button>
        ))}
        <label data-testid="wip-bulk-browse-btn" className="cursor-pointer px-4 py-2 bg-[#F5A524] text-black font-mono text-[11px] font-bold tracking-[0.2em] hover:bg-white">
          BROWSE FILES
          <input type="file" accept="image/*" multiple className="hidden" disabled={!!status} onChange={(e) => { run(e.target.files); e.target.value = ""; }} />
        </label>
      </div>
    </div>
  );
};

export const WipAdmin = () => (
  <AdminCollection name="wip" path="/wip" kicker="WORK IN PROGRESS / ASSETS" title="WIP & Assets" label="Item" Form={WipForm} Preview={Preview} Toolbar={BulkUpload} />
);
